// Lightweight, safe renderers for Steam Markdown & BBCode previews.

const escapeHtml = (s) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const inline = (s) => {
  let out = escapeHtml(s);
  out = out.replace(/\*\*(.+?)\*\*/g, '<strong class="text-zinc-100 font-semibold">$1</strong>');
  out = out.replace(/(^|[^*])\*(?!\s)([^*]+?)\*(?!\*)/g, '$1<em class="text-zinc-300 italic">$2</em>');
  out = out.replace(
    /\[(GAMEPLAY GIF[^\]]*)\]/gi,
    '<span class="inline-block my-1 px-2 py-1 border border-dashed border-teal-400/60 text-teal-300 text-xs font-mono rounded-xl">▶ $1</span>'
  );
  return out;
};

export function renderMarkdown(md) {
  if (!md) return "";
  const lines = md.split("\n");
  let html = "";
  let inList = false;
  const closeList = () => {
    if (inList) {
      html += "</ul>";
      inList = false;
    }
  };
  for (let raw of lines) {
    const line = raw.trimEnd();
    if (/^(---|___|\*\*\*)\s*$/.test(line.trim())) {
      closeList();
      html += '<hr class="border-zinc-800 my-4" />';
    } else if (/^###\s+/.test(line)) {
      closeList();
      html += `<h4 class="font-heading text-zinc-200 text-base font-bold tracking-tight mt-4 mb-1.5 uppercase">${inline(line.replace(/^###\s+/, ""))}</h4>`;
    } else if (/^##\s+/.test(line)) {
      closeList();
      html += `<h3 class="font-heading text-teal-400 text-lg font-bold tracking-tight mt-5 mb-2 uppercase">${inline(line.replace(/^##\s+/, ""))}</h3>`;
    } else if (/^#\s+/.test(line)) {
      closeList();
      html += `<h2 class="font-heading text-zinc-100 text-xl font-black tracking-tight mt-5 mb-2">${inline(line.replace(/^#\s+/, ""))}</h2>`;
    } else if (/^>\s?/.test(line)) {
      closeList();
      html += `<blockquote class="border-l-2 border-teal-400 pl-4 py-1 my-2 text-zinc-400 italic">${inline(line.replace(/^>\s?/, ""))}</blockquote>`;
    } else if (/^[-*]\s+/.test(line)) {
      if (!inList) {
        html += '<ul class="list-disc pl-5 space-y-1 my-2 text-zinc-300">';
        inList = true;
      }
      html += `<li>${inline(line.replace(/^[-*]\s+/, ""))}</li>`;
    } else if (line.trim() === "") {
      closeList();
      html += '<div class="h-2"></div>';
    } else {
      closeList();
      html += `<p class="text-zinc-300 my-1">${inline(line)}</p>`;
    }
  }
  closeList();
  return html;
}

export function renderBBCode(text) {
  if (!text) return "";
  let out = escapeHtml(text);
  out = out.replace(
    /\[h1\](.*?)\[\/h1\]/gis,
    '<div class="font-heading text-teal-400 text-lg font-bold uppercase tracking-tight mt-4 mb-2">$1</div>'
  );
  out = out.replace(
    /\[h([23])\](.*?)\[\/h\1\]/gis,
    '<div class="font-heading text-zinc-200 text-base font-bold uppercase tracking-tight mt-3 mb-1.5">$2</div>'
  );
  out = out.replace(/\[b\](.*?)\[\/b\]/gis, '<strong class="text-zinc-100 font-semibold">$1</strong>');
  out = out.replace(/\[i\](.*?)\[\/i\]/gis, "<em>$1</em>");
  out = out.replace(/\[u\](.*?)\[\/u\]/gis, '<span class="underline">$1</span>');
  out = out.replace(/\[quote\](.*?)\[\/quote\]/gis, '<blockquote class="border-l-2 border-teal-400 pl-4 my-2 text-zinc-400 italic">$1</blockquote>');
  out = out.replace(/\[url=?[^\]]*\](.*?)\[\/url\]/gis, '<span class="text-teal-400 underline">$1</span>');
  out = out.replace(/\[hr\]/gi, '<hr class="border-zinc-800 my-3" />');
  out = out.replace(/\[list\]([\s\S]*?)\[\/list\]/gi, (_, inner) => {
    const items = inner
      .split(/\[\*\]/)
      .map((s) => s.trim())
      .filter(Boolean)
      .map((s) => `<li>${s}</li>`)
      .join("");
    return `<ul class="list-disc pl-5 space-y-1 my-2 text-zinc-300">${items}</ul>`;
  });
  out = out.replace(/\n/g, "<br/>");
  return out;
}

export function stripBBCode(text) {
  if (!text) return "";
  return text
    .replace(/\[\/?(h1|h2|h3|b|i|u|list|quote)\]/gi, "")
    .replace(/\[hr\]/gi, "")
    .replace(/\[url=?[^\]]*\]/gi, "")
    .replace(/\[\/url\]/gi, "")
    .replace(/\[\*\]\s*/g, "- ")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
